import { CalendarDate } from '@internationalized/date';
import {
  enumUnit,
  enumWeightDisplayUnit,
  LoadLineItemInput,
  SalesItemLineType,
  SalesOrderLineItem,
} from '@rematter/frontend-lib-api-client';
import Big from 'big.js';

import {
  enumLoadWeightType,
  LoadFormLineItem,
  LoadFormState,
} from '../types/load';

import { toMoney } from './money';
import { convertSalesOrderQuantityToWeight } from './weight';

/**
 * Initial state for load create form.
 */
export const initialState = (): LoadFormState => ({
  bookingId: undefined,
  salesOrderId: undefined,
  facilityId: undefined,
  expectedShipDate: undefined,
  status: undefined,
  weightDisplayUnit: enumWeightDisplayUnit.PriceUnit,
  deliveryNumber: undefined,
  salesOrderNumber: undefined,
  salesOrderStatus: undefined,
  lineItems: [],
});

/**
 * Maps sales order line items to load form line items.
 */
export const mapSalesOrderLineItems = (
  lineItems: SalesOrderLineItem[],
): LoadFormLineItem[] =>
  lineItems.map((lineItem) => {
    const convertedWeight = convertSalesOrderQuantityToWeight(
      Number(lineItem.quantity),
      lineItem.unit,
    );

    return {
      id: lineItem.id,
      type: 'Raw',
      itemId: lineItem.item.id,
      tagId: null,
      unitPrice: toMoney(lineItem.unitPrice),
      estimatedPrice: toMoney(lineItem.totalPrice),
      pricingUnit: lineItem.unit,
      weights: {
        [enumLoadWeightType.Net]: null,
        [enumLoadWeightType.Gross]: null,
        [enumLoadWeightType.Tare]: null,
        [enumLoadWeightType.QuantityOwed]: convertedWeight,
      },
    };
  }) || [];

/**
 * Transforms a line item form data to the mutation input format.
 */
const transformLineItem = (item: LoadFormLineItem): LoadLineItemInput => ({
  type: item.type as SalesItemLineType,
  itemId: item.itemId || undefined,
  tagId: item.tagId || undefined,
  pricingUnit: item.pricingUnit || enumUnit.Pounds,
  weights: {
    [enumLoadWeightType.Gross]: {
      value: new Big(
        item.weights?.[enumLoadWeightType.Gross]?.value || 0,
      ).toNumber(),
      unit: item.weights?.[enumLoadWeightType.Gross]?.unit || enumUnit.Pounds,
    },
    [enumLoadWeightType.Tare]: {
      value: new Big(
        item.weights?.[enumLoadWeightType.Tare]?.value || 0,
      ).toNumber(),
      unit: item.weights?.[enumLoadWeightType.Tare]?.unit || enumUnit.Pounds,
    },
    [enumLoadWeightType.Net]: {
      value: new Big(
        item.weights?.[enumLoadWeightType.Net]?.value || 0,
      ).toNumber(),
      unit: item.weights?.[enumLoadWeightType.Net]?.unit || enumUnit.Pounds,
    },
  },
});

/**
 * Transforms LoadFormState data into the format required for the createLoad mutation.
 * Extracts all fields and properly maps line items with conditional logic.
 */
export const transformLoadCreateInput = (formData: LoadFormState) => ({
  expectedShipDate: formData.expectedShipDate as CalendarDate,
  salesOrderId: formData.salesOrderId ?? undefined,
  bookingId: formData.bookingId ?? undefined,
  facilityId: formData.facilityId as string,
  weightDisplayUnit:
    formData.weightDisplayUnit ?? enumWeightDisplayUnit.PriceUnit,
  deliveryNumber: formData.deliveryNumber ?? undefined,
  documentNumber: formData.loadNumber ?? undefined,
  lineItems: formData.lineItems.map((item) => transformLineItem(item)),
});
